import { useLockBodyScroll } from '@/hooks';
import { Icons } from '@/utils/Icons';
import { changePasswordSchema } from '@/utils/validation';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useChangePasswordMutation } from '@/store/api/userApi';
import { showToast } from '@/utils';
import { useNavigate } from 'react-router-dom';

type Props = {
  open: boolean;
  onClose: () => void;
};

type FormValues = z.infer<typeof changePasswordSchema>;

type InputFieldProps = {
  placeholder: string;
  show: boolean;
  toggle: () => void;
  name: keyof FormValues;
  register: ReturnType<typeof useForm<FormValues>>['register'];
  error?: string;
};

/* ✅ MOVED OUTSIDE (only real fix) */
const InputField: React.FC<InputFieldProps> = ({
  placeholder,
  show,
  toggle,
  name,
  register,
  error,
}) => (
  <div className="relative">
    <div className="flex items-center border border-soft rounded-md h-9 overflow-hidden">
      <input
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        className="flex-1 h-full px-2 text-[16px] font-normal placeholder:text-body-soft outline-none"
        {...register(name)}
        onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
      />

      <button
        type="button"
        onClick={toggle}
        className="w-10 h-full flex items-center justify-center"
      >
        {show ? <Icons.EyeSlash size={16} /> : <Icons.Eye size={16} />}
      </button>
    </div>

    {error && <p className="text-danger text-xs mt-1 ml-[5px] absolute w-full">{error}</p>}
  </div>
);

const ChangePasswordModal: React.FC<Props> = ({ open, onClose }) => {
  const [changePasswordMutation] = useChangePasswordMutation();
  const navigate = useNavigate();

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useLockBodyScroll(open);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onSubmit',
  });

  watch('newPassword'); // unchanged

  if (!open) return null;

  const onSubmit = async (data: FormValues) => {
    try {
      const result = await changePasswordMutation(data).unwrap();
      showToast(result?.message, 'success');
      navigate('/dashboard');
      reset();
      onClose();
    } catch (error: any) {
      showToast(error?.data?.message, 'error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary/40">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-[508px] bg-surface rounded-md shadow-auth-card mx-5"
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-2">
          <h2 className="text-[20px] font-medium">Change Password</h2>
        </div>

        {/* Form */}
        <div className="px-5 pb-5 space-y-6">
          <InputField
            placeholder="Enter old password"
            show={showOld}
            toggle={() => setShowOld(!showOld)}
            name="oldPassword"
            register={register}
            error={errors.oldPassword?.message}
          />

          <InputField
            placeholder="Enter new password"
            show={showNew}
            toggle={() => setShowNew(!showNew)}
            name="newPassword"
            register={register}
            error={errors.newPassword?.message}
          />

          <InputField
            placeholder="Confirm new password"
            show={showConfirm}
            toggle={() => setShowConfirm(!showConfirm)}
            name="confirmPassword"
            register={register}
            error={errors.confirmPassword?.message}
          />

          {/* Buttons */}
          <div className="flex justify-between pt-2">
            <button
              type="button"
              onClick={() => reset()}
              className="w-[104px] h-[38px] bg-primary text-white text-[16px]"
            >
              Reset
            </button>

            <button type="submit" className="w-[104px] h-[38px] bg-primary text-white text-[16px]">
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordModal;
